import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Undo2, Plus, EyeOff, Eye, ArrowUp, ArrowDown, FileText, Tags, Store, History, Search } from "lucide-react";
import { API, STORE, CATEGORIES } from "../../lib/store";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";

const STORE_FIELDS = [
  { key: "store.name", label: "Store name", def: STORE.name },
  { key: "store.tagline", label: "Tagline", def: STORE.tagline, multiline: true },
  { key: "store.address", label: "Address", def: STORE.address },
  { key: "store.phone", label: "Phone", def: STORE.phone },
  { key: "store.whatsapp", label: "WhatsApp number", def: STORE.whatsapp },
  { key: "store.email", label: "Email", def: STORE.email },
  { key: "store.hoursText", label: "Store hours (one per line)", def: STORE.hours.map((h) => `${h.d}: ${h.h}`).join("\n"), multiline: true },
  { key: "footer.blurb", label: "Footer blurb", def: "18k, 22k & 24k gold, silver & diamond jewelry. Visit our Chicago store or shop with USA-wide delivery.", multiline: true },
];

const SEO_PAGES = [
  { path: "/", label: "Home" },
  { path: "/shop", label: "Shop" },
  { path: "/collections", label: "Collections" },
  { path: "/contact", label: "Contact" },
  { path: "/book", label: "Book Appointment" },
  { path: "/education", label: "Education" },
  { path: "/stories", label: "Stories" },
];

const DEFAULT_CATS = CATEGORIES.map((c) => ({ key: c.key, label: c.label, blurb: c.blurb, image: "", hidden: false }));

const card = "bg-white border border-[#eee6d6] p-6";
const head = "font-heading text-xl text-[#171310] flex items-center gap-2 mb-4";

export function SiteEditorTab({ auth }) {
  const [content, setContent] = useState({});
  const [log, setLog] = useState([]);

  const loadContent = useCallback(async () => {
    try { const r = await axios.get(`${API}/content`); setContent(r.data || {}); } catch { /* ignore */ }
  }, []);
  const loadLog = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/content/log`, auth); setLog(r.data || []); } catch { /* ignore */ }
  }, [auth]);

  useEffect(() => { loadContent(); loadLog(); }, [loadContent, loadLog]);

  const saveKey = async (key, value, type = "text", prev = undefined) => {
    await axios.post(`${API}/admin/content`, { key, value, type, prev }, auth);
    setContent((m) => ({ ...m, [key]: value }));
    loadLog();
    toast.success("Saved — live now");
  };
  const undoKey = async (key) => {
    try {
      const r = await axios.post(`${API}/admin/content/undo`, { key }, auth);
      setContent((m) => ({ ...m, [key]: r.data.value }));
      loadLog();
      toast.success("Reverted to previous version");
    } catch (e) { toast.error(e?.response?.data?.detail || "Nothing to undo"); }
  };

  return (
    <div className="space-y-8" data-testid="site-editor-tab">
      <p className="text-sm text-[#7a7160]">
        Edit your site content here — changes go live instantly, no republish. You can also click <strong>Edit Site</strong> on any
        page to edit text and images right where they appear. Nothing is ever deleted — hide it instead and unhide from the Hidden list.
      </p>

      {/* Store & contact info */}
      <section className={card}>
        <h3 className={head}><Store size={18} /> Store &amp; Contact Info</h3>
        <div className="grid sm:grid-cols-2 gap-5">
          {STORE_FIELDS.map((f) => (
            <FieldRow key={f.key} field={f} value={content[f.key] ?? f.def} onSave={saveKey} onUndo={undoKey} />
          ))}
        </div>
      </section>

      {/* Categories */}
      <CategoriesEditor content={content} onSave={saveKey} onUndo={undoKey} />

      {/* SEO per page */}
      <section className={card}>
        <h3 className={head}><Search size={18} /> Page SEO (title &amp; description)</h3>
        <div className="space-y-5">
          {SEO_PAGES.map((p) => (
            <div key={p.path} className="border-b border-[#f0e9d8] pb-4 last:border-0">
              <p className="text-xs uppercase tracking-wider text-[#a8842c] mb-2">{p.label} <span className="text-[#c4baa4]">({p.path})</span></p>
              <FieldRow field={{ key: `seo${p.path === "/" ? "/home" : p.path}.title`.replace("//", "/"), label: "SEO title", def: "" }}
                value={content[`seo.${p.path}.title`] ?? ""}
                overrideKey={`seo.${p.path}.title`} onSave={saveKey} onUndo={undoKey} compact placeholder="Custom title for search engines" />
              <FieldRow field={{ key: `seo.${p.path}.description`, label: "SEO description", def: "", multiline: true }}
                value={content[`seo.${p.path}.description`] ?? ""}
                onSave={saveKey} onUndo={undoKey} compact placeholder="Meta description shown in Google" />
            </div>
          ))}
        </div>
      </section>

      {/* Pages */}
      <PagesEditor auth={auth} />

      {/* Change log */}
      <section className={card}>
        <h3 className={head}><History size={18} /> Recent Changes</h3>
        {log.length === 0 ? <p className="text-sm text-[#9c9482]">No edits yet.</p> : (
          <ul className="text-sm divide-y divide-[#f0e9d8] max-h-72 overflow-auto" data-testid="change-log">
            {log.map((e) => (
              <li key={e.id} className="py-2 flex items-center justify-between gap-3">
                <span className="text-[#5c5240]"><span className="uppercase text-[10px] tracking-wider text-[#a8842c] mr-2">{e.area}</span>{e.key} — <em>{e.action}</em></span>
                <span className="text-xs text-[#b0a68e] shrink-0">{new Date(e.at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function FieldRow({ field, value, onSave, onUndo, compact, placeholder, overrideKey }) {
  const key = overrideKey || field.key;
  const [val, setVal] = useState(value);
  useEffect(() => { setVal(value); }, [value]);
  const dirty = val !== value;
  return (
    <div className={compact ? "mb-3" : "space-y-1.5"}>
      {!compact && <Label className="text-xs uppercase tracking-wider text-[#7a7160]">{field.label}</Label>}
      {compact && <Label className="text-[11px] text-[#8f8571]">{field.label}</Label>}
      <div className="flex items-start gap-2">
        {field.multiline ? (
          <Textarea value={val} onChange={(e) => setVal(e.target.value)} rows={compact ? 2 : 3} placeholder={placeholder} className="rounded-none border-[#d8cdb5]" data-testid={`field-${key}`} />
        ) : (
          <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder={placeholder} className="rounded-none border-[#d8cdb5] h-10" data-testid={`field-${key}`} />
        )}
        <Button size="sm" onClick={() => onSave(key, val, field.multiline ? "html" : "text", value)} disabled={!dirty} className="rounded-none bg-[#171310] text-[#e9d9ac] h-10 px-3 shrink-0" data-testid={`save-${key}`}>
          <Save size={14} />
        </Button>
        <button onClick={() => onUndo(key)} title="Undo last change" className="text-[#b0a68e] hover:text-[#a8842c] mt-2.5 shrink-0"><Undo2 size={15} /></button>
      </div>
    </div>
  );
}

function CategoriesEditor({ content, onSave, onUndo }) {
  const stored = content["nav.categories"];
  const [cats, setCats] = useState(Array.isArray(stored) && stored.length ? stored : DEFAULT_CATS);
  useEffect(() => { if (Array.isArray(stored) && stored.length) setCats(stored); }, [stored]);

  const upd = (i, patch) => setCats((c) => c.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const move = (i, d) => setCats((c) => {
    const n = [...c]; const j = i + d;
    if (j < 0 || j >= n.length) return c;
    [n[i], n[j]] = [n[j], n[i]]; return n;
  });

  return (
    <section className={card}>
      <h3 className={head}><Tags size={18} /> Categories</h3>
      <div className="space-y-3">
        {cats.map((c, i) => (
          <div key={c.key || i} className={`border border-[#eee6d6] p-3 grid sm:grid-cols-[1fr_1fr_auto] gap-2 items-center ${c.hidden ? "opacity-60" : ""}`} data-testid={`cat-row-${c.key}`}>
            <Input value={c.label} onChange={(e) => upd(i, { label: e.target.value })} placeholder="Name" className="rounded-none border-[#d8cdb5] h-9" />
            <Input value={c.image || ""} onChange={(e) => upd(i, { image: e.target.value })} placeholder="Image URL (optional)" className="rounded-none border-[#d8cdb5] h-9" />
            <div className="flex items-center gap-1 justify-end">
              <button onClick={() => move(i, -1)} className="p-1 text-[#9c9482] hover:text-[#a8842c]"><ArrowUp size={15} /></button>
              <button onClick={() => move(i, 1)} className="p-1 text-[#9c9482] hover:text-[#a8842c]"><ArrowDown size={15} /></button>
              <button onClick={() => upd(i, { hidden: !c.hidden })} title={c.hidden ? "Unhide" : "Hide"} className="p-1 text-[#9c9482] hover:text-[#a8842c]" data-testid={`cat-hide-${c.key}`}>
                {c.hidden ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            <Input value={c.blurb || ""} onChange={(e) => upd(i, { blurb: e.target.value })} placeholder="Short description" className="rounded-none border-[#d8cdb5] h-9 sm:col-span-3" />
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-4">
        <Button onClick={() => onSave("nav.categories", cats, "json", stored)} className="rounded-none bg-[#171310] text-[#e9d9ac] text-xs" data-testid="save-categories">
          <Save size={14} className="mr-2" /> Save Categories
        </Button>
        <button onClick={() => onUndo("nav.categories")} className="text-xs flex items-center gap-1 text-[#7a7160] hover:text-[#a8842c]"><Undo2 size={13} /> Undo</button>
      </div>
    </section>
  );
}

const BLANK_PAGE = { slug: "", title: "", bodyHtml: "", image: "", seoTitle: "", seoDescription: "", inNav: false };

function PagesEditor({ auth }) {
  const [pages, setPages] = useState([]);
  const [draft, setDraft] = useState(BLANK_PAGE);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/pages`, auth); setPages(r.data || []); } catch { /* ignore */ }
  }, [auth]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!draft.title.trim()) return toast.error("Give the page a title");
    try {
      await axios.post(`${API}/admin/pages`, draft, auth);
      toast.success("Page saved — live now");
      setDraft(BLANK_PAGE);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const hide = async (slug, hidden) => {
    await axios.post(`${API}/admin/pages/${slug}/hide`, { hidden }, auth);
    load();
  };

  return (
    <section className={card}>
      <h3 className={head}><FileText size={18} /> Pages</h3>
      {pages.length > 0 && (
        <ul className="mb-6 divide-y divide-[#f0e9d8] border border-[#eee6d6]">
          {pages.map((p) => (
            <li key={p.slug} className="flex items-center justify-between gap-3 p-3" data-testid={`page-row-${p.slug}`}>
              <div className="min-w-0">
                <p className="font-medium text-[#171310] truncate">{p.title} {p.hidden && <span className="text-xs text-[#b0a68e]">(hidden)</span>}</p>
                <a href={`/p/${p.slug}`} target="_blank" rel="noreferrer" className="text-xs text-[#a8842c] hover:underline">/p/{p.slug}</a>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button size="sm" variant="outline" className="rounded-none border-[#d8cdb5] text-xs" onClick={() => setDraft({ ...BLANK_PAGE, ...p })}>Edit</Button>
                <button onClick={() => hide(p.slug, !p.hidden)} className="text-[#9c9482] hover:text-[#a8842c]" data-testid={`page-hide-${p.slug}`} title={p.hidden ? "Unhide" : "Hide"}>
                  {p.hidden ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="space-y-3 bg-[#faf7f0] border border-[#ece5d5] p-4">
        <p className="text-xs uppercase tracking-wider text-[#a8842c]">{draft.slug ? `Editing: ${draft.slug}` : "Add a new page"}</p>
        <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="Page title" className="rounded-none border-[#d8cdb5] h-10" data-testid="page-title" />
        <Input value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} placeholder="URL slug (optional — auto from title)" className="rounded-none border-[#d8cdb5] h-10" data-testid="page-slug" />
        <Textarea value={draft.bodyHtml} onChange={(e) => setDraft({ ...draft, bodyHtml: e.target.value })} rows={6} placeholder="Page content — plain text or simple HTML" className="rounded-none border-[#d8cdb5]" data-testid="page-body" />
        <Input value={draft.image || ""} onChange={(e) => setDraft({ ...draft, image: e.target.value })} placeholder="Header image URL (optional)" className="rounded-none border-[#d8cdb5] h-10" />
        <div className="grid sm:grid-cols-2 gap-3">
          <Input value={draft.seoTitle || ""} onChange={(e) => setDraft({ ...draft, seoTitle: e.target.value })} placeholder="SEO title" className="rounded-none border-[#d8cdb5] h-10" />
          <Input value={draft.seoDescription || ""} onChange={(e) => setDraft({ ...draft, seoDescription: e.target.value })} placeholder="SEO description" className="rounded-none border-[#d8cdb5] h-10" />
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={draft.inNav} onCheckedChange={(v) => setDraft({ ...draft, inNav: v })} data-testid="page-innav" />
          <span className="text-sm text-[#5c5240]">Show link in footer</span>
        </div>
        <div className="flex gap-2">
          <Button onClick={save} className="rounded-none bg-[#171310] text-[#e9d9ac] text-xs" data-testid="page-save"><Plus size={14} className="mr-2" /> {draft.slug ? "Save Page" : "Create Page"}</Button>
          {draft.slug && <Button variant="outline" onClick={() => setDraft(BLANK_PAGE)} className="rounded-none border-[#d8cdb5] text-xs">Cancel</Button>}
        </div>
      </div>
    </section>
  );
}
